import React, { MouseEventHandler, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useLazyQuery } from '@apollo/client';
import { SvgIcon } from '../Icon';
import {
  AnalysisStatus,
  IGetAnalysisInfoQuery,
  IGetAnalysisInfoQueryVariables,
  QueryType,
} from '../../utils/apollo/graphqlSchema';
import { queryTypeConfigLookup } from '../../utils/queryTypeLookup';
import BuilderInfo from '../BuilderInfo';
import KubitTooltip from '../KubitTooltip';
import { populateBuilderInfoAction } from '../../redux/slices/analyses';
import { globalFormat } from '../../utils/formatting';
import BuilderInfoLoader from '../BuilderInfo/BuilderInfoLoader';
import { GET_ANALYSIS_INFO } from '../../utils/apollo/analysis';
import { useAppDispatch } from '../../redux/hooks';
import { assertNotNil } from '../../utils/types';
import useAnalysisBuilderInfo from '../../redux/selectorHooks/useAnalysisBuilderInfo';
import { theme } from '../../styles/theme';
import EntityInfoPopover from '../EntityInfoPopover';
import { optionHover, overflowingStyles } from '../../styles/shareableStyles';
import ReportStatusBadge from '../ReportStatusBadge/ReportStatusBadge';

interface IProps {
  id: string;
  queryType: QueryType;
  display: string;
  status: AnalysisStatus;
  createdAt: string;
  url: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  isSelected: boolean;
}

const Container = styled(Link)<{ $isSelected?: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  height: 40px;
  padding-right: 8px;
  padding-left: 12px;
  border-radius: 4px;
  transition:
    background 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms,
    color 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms;
  will-change: background, color;
  color: ${theme.colors.secondary800};
  &:hover {
    ${optionHover};
  }
  ${({ $isSelected }) => $isSelected && optionHover}
`;

const ContentWrapper = styled.div`
  flex: 1;
  margin-bottom: 0 !important;
`;

const Header = styled.div`
  display: flex;
  margin-bottom: 4px;
`;

const SecondaryLinkAdditionalInfo = styled.div`
  font-size: 9px;
  font-weight: 400;
  color: ${theme.colors.secondary600};
`;

const Display = styled.div`
  max-width: 100%;
  margin-left: 5px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.33;
  ${overflowingStyles};
`;

export default ({
  id,
  queryType,
  display,
  status,
  createdAt,
  url,
  onClick,
  isSelected,
}: IProps) => {
  const [showInfo, setShowInfo] = useState(false);
  const [infoIsOpen, setInfoIsOpen] = useState(false);
  const { dateFormat, timeFormat } = globalFormat.getGlobalFormat();
  const dispatch = useAppDispatch();

  const analysisBuilderInfo = useAnalysisBuilderInfo(id);

  const handleLoadAnalysisInfo = ({ analysis }: IGetAnalysisInfoQuery) => {
    assertNotNil(analysis);
    dispatch(populateBuilderInfoAction({ analysisInfo: analysis }));
  };

  const [loadAnalysisInfo, { loading }] = useLazyQuery<
    IGetAnalysisInfoQuery,
    IGetAnalysisInfoQueryVariables
  >(GET_ANALYSIS_INFO, {
    variables: {
      analysisId: id,
    },
    onCompleted: handleLoadAnalysisInfo,
  });

  const handleMouseEnter = () => {
    if (!showInfo && !infoIsOpen) {
      setShowInfo(true);
    }
  };

  const handleMouseLeave = () => {
    if (showInfo && !infoIsOpen) {
      setShowInfo(false);
    }
  };

  const handleOpenInfoPopup = () => {
    setInfoIsOpen(true);

    // Run query in case when there is no analysis information in redux
    if (!analysisBuilderInfo) {
      loadAnalysisInfo();
    }
  };

  const handleCloseInfoPopup = () => {
    setInfoIsOpen(false);
    setShowInfo(false);
  };

  const showBuilderInfo =
    showInfo &&
    status === AnalysisStatus.Success &&
    queryType !== QueryType.Prediction;

  return (
    <Container
      to={url}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      $isSelected={isSelected}
    >
      <ContentWrapper>
        <Header>
          <SvgIcon
            name={queryTypeConfigLookup[queryType].icon}
            width={16}
            height={16}
            customFill="currentColor"
          />
          <KubitTooltip content={display} placement="bottom-start">
            <Display>{display}</Display>
          </KubitTooltip>
        </Header>
        <SecondaryLinkAdditionalInfo>
          {status !== AnalysisStatus.Success && (
            <ReportStatusBadge status={status} />
          )}
          {globalFormat.formatDate(createdAt, {
            dateFormat,
            timeFormat,
          })}
        </SecondaryLinkAdditionalInfo>
      </ContentWrapper>
      {showBuilderInfo && (
        <EntityInfoPopover
          onOpen={handleOpenInfoPopup}
          onClose={handleCloseInfoPopup}
          closeOnAncestorScroll={true}
        >
          {loading || !analysisBuilderInfo ? (
            <BuilderInfoLoader />
          ) : (
            <BuilderInfo
              kind="report"
              analysisId={id}
              onShowInReport={handleCloseInfoPopup}
              queryType={queryType}
            />
          )}
        </EntityInfoPopover>
      )}
    </Container>
  );
};
